import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ChevronUp, Globe2, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/data/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { cn } from '@/lib/cn';
import { useApiError } from '@/lib/useApiError';
import { useCopyPending, usePublishCopy } from '@/queries/copy';

/**
 * The publish bar.
 *
 * Drafts accumulate across keys and locales; publishing is one batch action
 * over all of them (plan Q2), which mints an immutable bundle. It goes live on
 * the public site and closing the tab does not undo it, so the confirm says
 * exactly that and the bundles screen — where revert lives — is one click away.
 */
export function PublishBar({
  onOpenKey,
  className,
}: {
  /** Jump to a pending key in the drawer. */
  onOpenKey?: (key: string) => void;
  className?: string;
}) {
  const { t } = useTranslation('copy');
  const { t: tc } = useTranslation('common');
  const resolveError = useApiError();
  const pending = useCopyPending();
  const publish = usePublishCopy();

  const [expanded, setExpanded] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const [note, setNote] = React.useState('');

  const count = pending.data?.count ?? 0;
  const drafts = pending.data?.drafts ?? [];
  const removals = pending.data?.removals ?? [];

  if (pending.isPending || count === 0) return null;

  async function onPublish() {
    try {
      const res = await publish.mutateAsync(note.trim() ? { note: note.trim() } : {});
      setConfirming(false);
      setNote('');
      setExpanded(false);
      if (res.published) {
        toast.success(
          t('publish.success', { version: res.version, count: res.changed_keys.length })
        );
      } else {
        toast.info(t('publish.nothing'));
      }
    } catch (e) {
      setConfirming(false);
      toast.error(resolveError(e).message);
    }
  }

  return (
    <div
      className={cn(
        'sticky bottom-0 z-30 -mx-4 mt-6 border-t border-[color:var(--color-border)] bg-[color:var(--color-surface)]/95 px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] backdrop-blur',
        className
      )}
    >
      {expanded ? (
        <div
          id="publish-pending-list"
          className="mb-3 max-h-64 overflow-y-auto rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface-2)] p-3"
        >
          <ul className="space-y-1 text-xs">
            {drafts.map((d) => (
              <li key={`${d.key}:${d.locale}`} className="flex flex-wrap items-center gap-2">
                <Badge tone="warn">{d.locale.toUpperCase()}</Badge>
                {onOpenKey ? (
                  <button
                    type="button"
                    dir="ltr"
                    onClick={() => onOpenKey(d.key)}
                    className="font-mono text-[color:var(--color-accent)] underline underline-offset-2"
                  >
                    {d.key}
                  </button>
                ) : (
                  <span dir="ltr" className="font-mono">
                    {d.key}
                  </span>
                )}
                <span className="text-[color:var(--color-fg-muted)]">{d.surface}</span>
              </li>
            ))}
            {removals.map((r) => (
              <li key={`rm:${r.key}:${r.locale}`} className="flex flex-wrap items-center gap-2">
                <Badge tone="danger">{r.locale.toUpperCase()}</Badge>
                <span dir="ltr" className="font-mono line-through">
                  {r.key}
                </span>
                <span className="text-[color:var(--color-fg-muted)]">{t('publish.removal')}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Badge tone="warn" className="gap-1">
            <Globe2 className="size-3.5" aria-hidden />
            {t('publish.pendingCount', { count })}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={expanded}
            aria-controls="publish-pending-list"
            onClick={() => setExpanded((v) => !v)}
          >
            <ChevronUp className={cn('size-4 transition-transform', !expanded && 'rotate-180')} aria-hidden />
            {expanded ? t('publish.hideList') : t('publish.showList')}
          </Button>
          {pending.isFetching ? (
            <Loader2 className="size-4 animate-spin text-[color:var(--color-fg-muted)]" aria-hidden />
          ) : null}
        </div>

        <div className="flex flex-1 flex-wrap items-end justify-end gap-2">
          <div className="min-w-48 flex-1 sm:max-w-sm">
            <Label htmlFor="publish-note" className="mb-1 text-xs">
              {t('publish.noteLabel')}
            </Label>
            <Input
              id="publish-note"
              value={note}
              maxLength={500}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('publish.notePlaceholder')}
            />
          </div>
          <Button onClick={() => setConfirming(true)} loading={publish.isPending}>
            <Globe2 className="size-4" aria-hidden />
            {t('publish.action')}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t('publish.confirmTitle')}
        description={t('publish.confirmBody', { count })}
        confirmLabel={t('publish.confirmAction')}
        cancelLabel={tc('actions.cancel')}
        loading={publish.isPending}
        onConfirm={onPublish}
      />
    </div>
  );
}
